/**
 * Локальный прогресс обучения (E4.6a): уроки, задания, «Первые шаги» и активный учебный
 * экземпляр. Ключ qh.training.v1:<companyId>:<userId> -- другой пользователь той же
 * вкладки чужих отметок не видит. Чтение строгое: всё, чего нет в каталоге, отбрасывается;
 * урок завершён, только если просмотрен каждый его шаг; цели задания засчитываются по
 * порядку и принадлежат экземпляру, в котором выполнены: другой или сброшенный пример
 * начинает незавершённое задание заново. Значение неизвестной версии схемы не перезаписывается.
 */

import {
	EXERCISE_IDS,
	EXERCISES,
	LESSON_IDS,
	LESSONS,
	PRACTICE_IDS,
	type ExerciseId,
	type LessonId,
	type PracticeId,
} from './catalog';
import { readLocal, writeLocal } from './storage';

export const TRAINING_PROGRESS_SCHEMA = 1;

export interface LessonProgress {
	cursor: number;
	seen: number[];
	status: 'started' | 'completed';
}

export interface ExerciseProgress {
	done: string[];
	status: 'started' | 'completed';
	/** Учебный экземпляр, в котором выполнены цели */
	instance_id: string | null;
}

export interface TrainingProgress {
	schema_version: 1;
	active_instance_id: string | null;
	lessons: Partial<Record<LessonId, LessonProgress>>;
	exercises: Partial<Record<ExerciseId, ExerciseProgress>>;
	practice: Partial<Record<PracticeId, boolean>>;
	dismissed: boolean;
	last: LessonId | null;
}

/** Почему прогресс не прочитан из браузера: показывается пользователю */
export type ProgressWarning = 'storage_unavailable' | 'corrupted' | 'unknown_schema';

export function trainingProgressKey(companyId: string, userId: string): string {
	return `qh.training.v${TRAINING_PROGRESS_SCHEMA}:${companyId}:${userId}`;
}

export function emptyProgress(): TrainingProgress {
	return {
		schema_version: 1,
		active_instance_id: null,
		lessons: {},
		exercises: {},
		practice: {},
		dismissed: false,
		last: null,
	};
}

function isLesson(value: unknown): value is LessonId {
	return typeof value === 'string' && (LESSON_IDS as readonly string[]).includes(value);
}

function validStep(lesson: LessonId, index: unknown): index is number {
	return (
		Number.isInteger(index) &&
		(index as number) >= 0 &&
		(index as number) < LESSONS[lesson].steps.length
	);
}

function readLesson(lesson: LessonId, raw: unknown): LessonProgress | null {
	if (!raw || typeof raw !== 'object') return null;
	const saved = raw as { cursor?: unknown; seen?: unknown; status?: unknown };
	const seen = [
		...new Set((Array.isArray(saved.seen) ? saved.seen : []).filter((i) => validStep(lesson, i))),
	];
	return {
		cursor: validStep(lesson, saved.cursor) ? saved.cursor : 0,
		seen,
		status:
			saved.status === 'completed' && seen.length === LESSONS[lesson].steps.length
				? 'completed'
				: 'started',
	};
}

/** Цели задания засчитываются по порядку: первая непройденная обрывает список */
function orderedGoals(exercise: ExerciseId, done: readonly unknown[]): string[] {
	const goals = EXERCISES[exercise].goals;
	const result: string[] = [];
	for (let i = 0; i < goals.length && done[i] === goals[i]; i++) result.push(goals[i]);
	return result;
}

function exerciseProgress(
	exercise: ExerciseId,
	done: string[],
	instanceId: string | null,
): ExerciseProgress {
	return {
		done,
		status: done.length === EXERCISES[exercise].goals.length ? 'completed' : 'started',
		instance_id: instanceId,
	};
}

function parse(raw: unknown): TrainingProgress {
	const value = raw as Record<string, unknown>;
	const progress = emptyProgress();
	progress.active_instance_id =
		typeof value.active_instance_id === 'string' ? value.active_instance_id : null;
	progress.dismissed = value.dismissed === true;
	progress.last = isLesson(value.last) ? value.last : null;
	const lessons = (value.lessons ?? {}) as Record<string, unknown>;
	LESSON_IDS.forEach((lesson) => {
		const read = readLesson(lesson, lessons[lesson]);
		if (read) progress.lessons[lesson] = read;
	});
	const exercises = (value.exercises ?? {}) as Record<
		string,
		{ done?: unknown; instance_id?: unknown }
	>;
	EXERCISE_IDS.forEach((exercise) => {
		const done = exercises[exercise]?.done;
		/* Задание, сохранённое до привязки к экземпляру, относится к активному тогда примеру */
		const instance = exercises[exercise]?.instance_id;
		if (Array.isArray(done))
			progress.exercises[exercise] = exerciseProgress(
				exercise,
				orderedGoals(exercise, done),
				typeof instance === 'string' ? instance : progress.active_instance_id,
			);
	});
	const practice = (value.practice ?? {}) as Record<string, unknown>;
	PRACTICE_IDS.forEach((id) => {
		if (practice[id] === true) progress.practice[id] = true;
	});
	return progress;
}

export function readTrainingProgress(
	companyId: string,
	userId: string,
): { progress: TrainingProgress; warning: ProgressWarning | null } {
	const { value, available } = readLocal(trainingProgressKey(companyId, userId));
	if (!available) return { progress: emptyProgress(), warning: 'storage_unavailable' };
	if (value === null) return { progress: emptyProgress(), warning: null };
	let raw: unknown;
	try {
		raw = JSON.parse(value);
	} catch {
		return { progress: emptyProgress(), warning: 'corrupted' };
	}
	if (!raw || typeof raw !== 'object') return { progress: emptyProgress(), warning: 'corrupted' };
	if ((raw as { schema_version?: unknown }).schema_version !== TRAINING_PROGRESS_SCHEMA) {
		return { progress: emptyProgress(), warning: 'unknown_schema' };
	}
	return { progress: parse(raw), warning: null };
}

export function loadTrainingProgress(companyId: string, userId: string): TrainingProgress {
	return readTrainingProgress(companyId, userId).progress;
}

/** Запись прогресса; значение другой версии схемы не затирается */
export function saveTrainingProgress(
	companyId: string,
	userId: string,
	value: TrainingProgress,
): { persisted: boolean } {
	const key = trainingProgressKey(companyId, userId);
	if (readTrainingProgress(companyId, userId).warning === 'unknown_schema')
		return { persisted: false };
	return { persisted: writeLocal(key, JSON.stringify(value)) };
}

/** Просмотр шага урока: курсор, отметка просмотра без повторов, последний урок */
export function recordStep(
	progress: TrainingProgress,
	lesson: LessonId,
	index: number,
): TrainingProgress {
	if (!isLesson(lesson) || !validStep(lesson, index)) return progress;
	const current = progress.lessons[lesson] ?? { cursor: 0, seen: [], status: 'started' as const };
	return {
		...progress,
		lessons: {
			...progress.lessons,
			[lesson]: {
				...current,
				cursor: index,
				seen: current.seen.includes(index) ? current.seen : [...current.seen, index],
			},
		},
		last: lesson,
	};
}

/** Завершение урока: только если просмотрен каждый шаг */
export function completeLesson(
	progress: TrainingProgress,
	lesson: LessonId,
): { progress: TrainingProgress; completed: boolean } {
	const current = progress.lessons[lesson];
	if (!current || current.seen.length !== LESSONS[lesson].steps.length)
		return { progress, completed: false };
	return {
		progress: {
			...progress,
			lessons: { ...progress.lessons, [lesson]: { ...current, status: 'completed' } },
		},
		completed: true,
	};
}

/** Цели задания в активном экземпляре: начатые в другом не продолжаются */
function doneHere(progress: TrainingProgress, exercise: ExerciseId): string[] {
	const saved = progress.exercises[exercise];
	return saved && saved.instance_id === progress.active_instance_id ? saved.done : [];
}

/** Выполненная цель задания: засчитывается, только если все предыдущие уже выполнены */
export function recordExerciseGoal(
	progress: TrainingProgress,
	exercise: ExerciseId,
	goal: string,
): TrainingProgress {
	const done = doneHere(progress, exercise);
	if (done.includes(goal) || EXERCISES[exercise].goals[done.length] !== goal) return progress;
	return {
		...progress,
		exercises: {
			...progress.exercises,
			[exercise]: exerciseProgress(exercise, [...done, goal], progress.active_instance_id),
		},
	};
}

/** Начать задание: заново -- с пустыми целями; иначе продолжить начатое */
export function startExercise(
	progress: TrainingProgress,
	exercise: ExerciseId,
	restart: boolean,
): TrainingProgress {
	const saved = progress.exercises[exercise];
	if (saved && !restart && saved.instance_id === progress.active_instance_id) return progress;
	return {
		...progress,
		exercises: {
			...progress.exercises,
			[exercise]: exerciseProgress(exercise, [], progress.active_instance_id),
		},
	};
}

export function recordPractice(progress: TrainingProgress, practice: PracticeId): TrainingProgress {
	return { ...progress, practice: { ...progress.practice, [practice]: true } };
}

export function setFirstStepsDismissed(
	progress: TrainingProgress,
	dismissed: boolean,
): TrainingProgress {
	return { ...progress, dismissed };
}

/** Незавершённые задания -- с пустыми целями в экземпляре instanceId, пройденные остаются */
function restartUnfinished(
	progress: TrainingProgress,
	instanceId: string | null,
): TrainingProgress {
	const exercises: TrainingProgress['exercises'] = {};
	EXERCISE_IDS.forEach((exercise) => {
		const saved = progress.exercises[exercise];
		if (saved)
			exercises[exercise] =
				saved.status === 'completed' ? saved : exerciseProgress(exercise, [], instanceId);
	});
	return { ...progress, active_instance_id: instanceId, exercises };
}

/**
 * Новый учебный пример: уроки и «Первые шаги» сохраняются до отдельного сброса,
 * незавершённое задание начинается в новом экземпляре с пустыми целями.
 */
export function startNewExample(progress: TrainingProgress, instanceId: string): TrainingProgress {
	return restartUnfinished(progress, instanceId);
}

/** Сброс примера: состояние экземпляра исходное, незавершённые задания -- заново */
export function restartUnfinishedExercises(progress: TrainingProgress): TrainingProgress {
	return restartUnfinished(progress, progress.active_instance_id);
}

/** Другой активный экземпляр: незавершённые задания начинаются в нём заново */
export function setActiveInstance(
	progress: TrainingProgress,
	instanceId: string | null,
): TrainingProgress {
	return instanceId === progress.active_instance_id
		? progress
		: restartUnfinished(progress, instanceId);
}
